import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { IBank, IBPProfile, ICurrency2, IExtendedWindow, IValueObject } from '@type_definitions/datatypes';
import { BPProfilesService } from '../../bp-profiles.service';
import _ from 'underscore';
import { urlUtil, util } from '@app/shared/util';
import { BPProfilesDataService } from '../../bp-profiles-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { GaMessageService, IDialog } from '@app/shared/message/ga-message.service';
import { PaypalConnectDialogComponent } from './paypal-connect-dialog.component';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { Subscription } from 'rxjs';

import { FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { CountryDataService } from '@app/shared/location/country-data.service';
import { LOV_BANK_COSTS_CHARGE_BEARER, LOV_CURRENCY, LovService } from '@app/shared/services/lov.service';
import { BaseBPProfilesDetailComponent } from '../base-bp-profiles-detail.component';
import { Override } from '@app/shared/app-decorators';
import { AMOUNT_CURRENCY_REGEXP, GaValidateService } from '@app/shared/formly/ga-validate.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { UntypedFormGroup } from '@angular/forms';
import { FAQ_BP_BANK_INFO } from '@app/shared/faq/faq-codes';

export enum BankTab {
  LOCAL = 'localBank',
  EUR = 'euroBank',
  USD = 'usdBank'
}

export interface IBPProfileBankModel extends Partial<IBank> {
  addressGroup?:string;
}

export const TRANSLATION_SCREEN:string = 'BPPROFILES.BANK';

export interface IBankFormChange {
  model: { [key:string]:IBPProfileBankModel };
  fields:IExtraFieldConfigurationObject[];
  form:UntypedFormGroup;
  selectedTab:string;
}

@Component({
  selector: 'bp-profiles-bank',
  template: `
    <h2 *ngIf="loading"><activity-indicator></activity-indicator></h2>
    
    <div *ngIf="!loading">
      <div class="btn-group bank-selector top-form-buttons">
        <button *ngFor="let tab of tabs" class="btn" [class.active]="selectedTab === tab.id" (click)="selectTab(tab.id)" [busyButton]="tabsBusy && selectedTab === tab.id" [disabled]="tabsBusy && selectedTab !== tab.id">{{tab.value}}</button>
      </div>

      <p class="margin-top-sm">
        <text screen="${TRANSLATION_SCREEN}" key="BANK_DETAIL"></text>
        <faq-popup faqCode="${FAQ_BP_BANK_INFO}" class="d-block margin-top-sm"><i class="icon fa fa-info-circle"></i> {{'BPPROFILES.BANK.MORE_INFO.TEXT' | translate}}</faq-popup>
      </p>
      
      <formly [form]="form" [fields]="fields" [model]="model"></formly>

      <div class="row" *ngIf="!editingMode && showOnlinePaymentConnection">
        <hr>
        <div class="col-md-6 col-space-bottom">
          <div class="alert alert-success" *ngIf="profileDetail.stripeConnected" translate>BPPROFILES.BANK.STRIPE_CONNECTED</div>
          <div *ngIf="!profileDetail.stripeConnected">
            <a class="btn btn-sm btn-primary btn-stripe" [href]="stripeConnectUrl"><i class="fa fa-cc-stripe margin-right-xs"></i> <span translate>BPPROFILES.BANK.STRIPE_CONNECT</span></a>
            <div class="info-row">
              <div class="info">
                <text key="STRIPE_INFO"></text>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="alert alert-success" *ngIf="profileDetail.paypalConnected" translate>BPPROFILES.BANK.PAYPAL_CONNECTED</div>
          <div *ngIf="!profileDetail.paypalConnected">
            <button class="btn btn-sm btn-primary btn-paypal" (click)="connectWithPaypal()"><i class="fa fa-paypal"></i> <span translate>BPPROFILES.BANK.PAYPAL_CONNECT</span></button>
            <div class="info-row">
              <div class="info">
                <text key="PAYPAL_INFO"></text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./bp-profiles-bank.scss']
})
export class BPProfilesBankComponent extends BaseBPProfilesDetailComponent {
  @Input() public showOnlinePaymentConnection:boolean;
  @Input() public editingMode:boolean;
  @Input() public saveFn:() => Promise<IBPProfile>;

  @Output() public onBankFormChange:EventEmitter<IBankFormChange> = new EventEmitter<IBankFormChange>();

  public stripeConnectUrl:string;
  public tabsBusy:boolean;
  public model:IBPProfileBankModel = {};
  public tabs:IValueObject[];

  private _profileDetail:IBPProfile;
  private _selectedTab:string;
  private valueChangesSubscription:Subscription;
  private onSaveBusySubscription:Subscription;
  private countryField:IExtraFieldConfigurationObject;
  private currencyField:IExtraFieldConfigurationObject;
  private bankCostsChargeBearerField:IExtraFieldConfigurationObject;
  private bankCostsField:IExtraFieldConfigurationObject;

  constructor(
    private bpProfilesDataService:BPProfilesDataService,
    private translationService:TranslationService,
    private messageService:GaMessageService,
    private authService:AuthService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private countryDataService:CountryDataService,
    private lovService:LovService,
    private validateService:GaValidateService,
    private deferredService:GaDeferredService,
    protected bpProfilesService:BPProfilesService,
    @Inject(Window) private window:IExtendedWindow
  ) {
    super(bpProfilesService);
  }

  @Override(BaseBPProfilesDetailComponent)
  public ngOnInit():void {
    this.editingMode = util.notNullOrUndefinedOrEmpty(this.editingMode) ? this.editingMode : this.bpProfilesService.editingMode;
    // create tabs
    this.tabs = this.createTabs();
    super.ngOnInit();
  }

  @Override(BaseBPProfilesDetailComponent)
  public onProfileDetailLoaded(profileDetail:IBPProfile):void {
    this.profileDetail = util.deepClone(profileDetail);
    this.setStripeConnectUrl();
    // set default tab
    this.selectedTab = BankTab.LOCAL;
    // create formly fields
    this.fields = this.createFields();

    if (this.editingMode) {
      this.onCurrencyChange(this.model.paymentCurrency);
      this.fillFields();
    }
  }

  @Override(BaseBPProfilesDetailComponent)
  public listen():void {
    this.onEditChangeSubscription = this.bpProfilesService.onEditChange.subscribe((isEdit:boolean) => {
      this.editingMode = isEdit;

      // re-render formly fields
      this.fields = this.createFields();
      this.onCurrencyChange(this.model.paymentCurrency);

      if (!this.editingMode) {
        this.resetModel();
      } else {
        this.fillFields();
      }
    });

    this.onSaveSubscription = this.bpProfilesService.onSave.subscribe(this.onSave);
    this.onSaveBusySubscription = this.bpProfilesService.onSaveBusy.subscribe((isBusy:boolean) => {
      this.tabsBusy = isBusy;
    });
    this.valueChangesSubscription = this.form.valueChanges.subscribe(this.emitBankFormChange);
  }

  public get profileDetail():IBPProfile {
    return this._profileDetail;
  }

  public set profileDetail(profileDetail:IBPProfile) {
    this._profileDetail = profileDetail;
    this.bpProfilesService.profileDetail = profileDetail;
  }

  public get selectedTab():string {
    return this._selectedTab;
  }

  public set selectedTab(selectedTab:string) {
    this._selectedTab = selectedTab;
    this.model = this.getModel();
  }

  private get isModelChange():boolean {
    let isModelChange:boolean = false;
    _.each(this.getRequestModel() || {}, (value:any, key:string) => {
      if (!_.isEqual(value || undefined, this.profileDetail[this.selectedTab]?.[key])) {
        isModelChange = true;
      }
    });
    return isModelChange;
  }

  private emitBankFormChange = ():void => {
    this.onBankFormChange.emit({ model: { [this.selectedTab]: this.getRequestModel() }, fields: this.fields, form: this.form, selectedTab: this.selectedTab });
  };

  public selectTab(id:string):void {
    if (this.editingMode && this.isModelChange) {
      this.onSave()
        .finally(() => {
          this.selectedTab = id;
        });
    } else {
      this.selectedTab = id;
    }
  }

  public connectWithPaypal():void {
    const dialog:IDialog = this.messageService.showDialog({
      component: PaypalConnectDialogComponent,
      wrapperComponent: false,
      inputs: {
        profileId: this.bpProfilesService.activeProfile?.id
      },
      outputs: {
        onCancel: () => dialog.hide(),
        onSaved: () => {
          this.messageService.showFeedbackMessage('BPPROFILES.BANK.PAYPAL_CONNECT_SUCCESS');
          const updatedProfileDetail:IBPProfile = _.clone(this.profileDetail);
          updatedProfileDetail.paypalConnected = true;
          // update profile detail data
          this.profileDetail = updatedProfileDetail;
          dialog.hide();
        }
      }
    });
  }

  /**
   * @return Promise<IBPProfile | any> - 'any' is for when promise has rejection we do not want to do anything there
   */
  protected onSave = ():Promise<IBPProfile | any> => {
    // when save function is defined in input bindings then execute this function
    if (this.saveFn) {
      this.setSaveBusy(true);
      return this.saveFn()
        .then(() => {
          this.profileDetail = util.copyAndExtend(this.bpProfilesService.profileDetail);
          this.form.patchValue(this.getModel());
        })
        .finally(() => {
          this.setSaveBusy(false);
        });
    } else {
      const deferred:IGADeferred<void> = this.deferredService.defer();
      this.validateService.validateFields(this.fields, this.model, true)
        .then(() => {
          const data:IBPProfile = util.copyAndExtend(this.bpProfilesService.profileDetail, { [this.selectedTab]: this.getRequestModel() });
          this.setSaveBusy(true);
          this.bpProfilesDataService.updateBPProfileDetail(this.bpProfilesService.activeProfile?.id, data, this.translationService.instant('BPPROFILES.BANK.TEXT'))
            .then((profileDetail:IBPProfile) => {
              this.profileDetail = util.copyAndExtend(profileDetail);
              this.form.patchValue(this.getModel());
              deferred.resolve();
            })
            .catch(util.noop)
            .finally(() => {
              this.setSaveBusy(false);
            });
        })
        .catch(deferred.reject);

      return deferred.promise;
    }
  };

  /**
   * @description this model will be used when sending http request to server
   * @return IBank
   */
  private getRequestModel = ():Partial<IBank> => {
    const preparedModel:IBPProfileBankModel = util.deepClone(this.model);
    delete preparedModel.addressGroup;
    return preparedModel;
  };

  /**
   * @description this will be used for formly purposes
   * @return IBPProfileBankModel
   */
  private getModel():IBPProfileBankModel {
    const model:IBPProfileBankModel = util.deepClone(this.profileDetail[this.selectedTab] || {});
    model.addressGroup = this.getFormattedAddress(model);
    return model;
  }

  @Override(BaseBPProfilesDetailComponent)
  public resetModel():void {
    if (this.selectedTab) {
      this.form.patchValue(this.profileDetail[this.selectedTab]);
      this.form.markAsPristine();
    }
  }

  private createTabs():IValueObject[] {
    return [
      {
        id: BankTab.LOCAL,
        value: this.translationService.instant('BPPROFILES.BANK.LOCAL_CURRENCY.LABEL')
      },
      {
        id: BankTab.USD,
        value: 'USD'
      },
      {
        id: BankTab.EUR,
        value: 'Euro'
      }
    ];
  }

  protected fillFields():void {
    this.formlyService.fillField(this.countryField, this.countryDataService.getAllCountries());
    this.formlyService.fillField(this.currencyField, this.lovService.list(LOV_CURRENCY));
    this.formlyService.fillField(this.bankCostsChargeBearerField, this.lovService.list(LOV_BANK_COSTS_CHARGE_BEARER));
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    const fullWidthClass:string = 'col-space-bottom col-md-12';
    const isUsdOrEur:boolean = [BankTab.USD.toString(), BankTab.EUR.toString()].indexOf(this.selectedTab) > -1;

    return [
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'bankName')
        .withPlaceholder('BPPROFILES.BANK.BANKNAME.LABEL')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.GROUP : FormlyType.VALUE, 'addressGroup')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withFields([
          this.formlyService.anInput('streetAddress')
            .withPlaceholder()
            .withoutWrapper()
            .withWrapperClass(fullWidthClass)
            .build(),
          this.formlyService.anInput('zipCodeAndCity')
            .withPlaceholder()
            .withoutWrapper()
            .withWrapperClass(fullWidthClass)
            .build(),
          this.countryField = this.formlyService.aSelect('country')
            .withoutWrapper()
            .withWrapperClass(fullWidthClass)
            .withFormat(this.formattingService.formatCountry)
            .build()
        ])
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'iban')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withPlaceholder('BPPROFILES.BANK.IBAN.LABEL')
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'accountNumber')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withPlaceholder('BPPROFILES.BANK.ACCOUNTNUMBER.LABEL')
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'swiftCode')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withPlaceholder('BPPROFILES.BANK.SWIFTCODE.LABEL')
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'bankCode')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withPlaceholder('BPPROFILES.BANK.BANKCODE.LABEL')
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'abaNumber')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withPlaceholder('BPPROFILES.BANK.ABANUMBER.LABEL')
        .build(),
      this.currencyField = this.formlyService.aField(this.editingMode && !isUsdOrEur? FormlyType.SELECT : FormlyType.VALUE, 'paymentCurrency')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withFormat(this.formattingService.formatCurrencyL)
        .withExtraOptions({
          templateOptions: {
            trackBy: 'isoCode'
          }
        })
        .onChange(this.onCurrencyChange)
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'accountHolder')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withPlaceholder('BPPROFILES.BANK.ACCOUNTHOLDER.LABEL')
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'specialBankInstruction')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withPlaceholder('BPPROFILES.BANK.SPECIALBANKINSTRUCTION.LABEL')
        .build(),
      this.bankCostsField = this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'bankCosts')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withInputType(FormlyType.NUMBER)
        .withValueClass('col-lg-4 col-md-6')
        .withPattern(AMOUNT_CURRENCY_REGEXP)
        .withValidationMessage('pattern', this.translationService.instant('GA.VALIDATION.INVALID', { label: 'BPPROFILES.BANK.BANKCOSTS.LABEL' }))
        .withExtraOptions({
          templateOptions: {
            min: this.editingMode ? 0 : undefined,
            showAddOn: this.editingMode
          }
        })
        .build(),
      this.bankCostsChargeBearerField = this.formlyService.aField(this.editingMode? FormlyType.SELECT : FormlyType.VALUE, 'bankCostsChargeBearer')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withFormat(this.formattingService.formatLov)
        .build(),
      this.formlyService.aValue('modificationDate','BPPROFILES.BANK.LASTUPDATEDATE.LABEL')
        .withFormat(this.formattingService.formatDate)
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withPlaceholder('BPPROFILES.BANK.LASTUPDATEDATE.LABEL')
        .build(),
      this.formlyService.aValue('modificationUser', 'BPPROFILES.BANK.LASTUPDATEBY.LABEL')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withPlaceholder('BPPROFILES.BANK.SPECIALBANKINSTRUCTION.LABEL')
        .build()
    ];
  }

  private onCurrencyChange = (currency:ICurrency2):void => {
    this.bankCostsField.templateOptions.addOn = currency?.isoCode;
  };

  private setStripeConnectUrl():void {
    let url:string = 'rest/stripe-connect/authorize';
    url = urlUtil.addQueryParams(url, {
      url: this.window.location.href,
      ccId: this.bpProfilesService.activeProfile?.companyCountryId,
      embedded: false
    });

    this.stripeConnectUrl = this.authService.authenticatedUrl(url);
  }

  private getFormattedAddress(model:IBPProfileBankModel):string {
    return this.formattingService.formatSimpleAddress(
      model?.streetAddress,
      model?.zipCodeAndCity,
      model?.country?.name
    );
  }

  @Override(BaseBPProfilesDetailComponent)
  public ngOnDestroy():void {
    super.ngOnDestroy();
    this.onSaveBusySubscription?.unsubscribe();
    this.valueChangesSubscription?.unsubscribe();
  }
}